
import { useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface PricingPlan {
  plan_id: string;
  plan_name: string;
  original_price: number;
  discount: {
    type: 'fixed' | 'percentage';
    value: number;
    amount: number;
  } | null;
  price_after_discount: number;
  tax_rate: number;
  tax_amount: number;
  final_amount: number;
  duration: number;
  type: string;
}

interface ProductInfo {
  id: string;
  name: string;
  features: string | null;
  description: string | null;
  logo?: string;
  product_screen?: string;
}

interface PricingResponse {
  product: ProductInfo;
  plans: PricingPlan[];
}

export const useExternalPricing = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [pricingData, setPricingData] = useState<PricingResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fetchCompleted, setFetchCompleted] = useState(false);

  const fetchPricingPlans = async (schoolId: string, productName: string) => {
    // Skip if we've already completed a fetch
    if (fetchCompleted) {
      console.log("Pricing plans fetch already completed, skipping");
      return pricingData;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log("Calling get-pricing-plans edge function for:", schoolId, productName);
      const { data, error } = await supabase.functions.invoke(
        'get-pricing-plans',
        {
          body: { schoolId, productName }
        }
      );
      
      if (error) {
        console.error("Error from pricing plans function:", error);
        throw new Error(error.message || 'Failed to fetch pricing plans');
      }
      
      console.log("Received pricing data:", data);
      setPricingData(data as PricingResponse);
      setFetchCompleted(true);
      return data as PricingResponse;
    } catch (err: any) {
      console.error('Error fetching pricing plans:', err);
      const errorMessage = err.message || 'An error occurred while fetching pricing plans';
      setError(errorMessage);
      
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
      
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    pricingData,
    error,
    fetchPricingPlans,
    fetchCompleted
  };
};
